import React, { useState, useEffect } from "react";
import { CardBody, Row, Col, Card, Table, CardHeader, Container, Button } from "reactstrap";
import { useLocation, Link } from "react-router-dom";
import { fetchOrder } from '../../../utils/order';
import { getLoggedinUser } from '../../../utils/api_helper_custom';
import { getBestProfilePhoto } from '../../../utils/profilePhotoUtils';
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import logoDark from "../../../assets/images/logo-dark.png";
import logoLight from "../../../assets/images/logo-light.png";
import avatar3 from "../../../assets/images/users/avatar-3.jpg";

const OrderInvoice = () => {
  const [orderData, setOrderData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get("orderId");

  // Print the Invoice
  const printInvoice = () => {
    window.print();
  };

  useEffect(() => {
    const loadOrderData = async () => {
      if (!orderId) {
        setError("No order ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetchOrder(orderId);
        
        if (response.success) {
          const order = response.order_data;
          setOrderData(order);
          
          // Set customer data from session
          const currentUser = getLoggedinUser();
          if (currentUser) {
            setCustomerData({
              name: currentUser.name || 
                    (currentUser.first_name && currentUser.last_name ? 
                      `${currentUser.first_name} ${currentUser.last_name}` : 
                      currentUser.first_name || currentUser.last_name || 'Customer'),
              email: currentUser.email || 'customer@example.com',
              phoneNumber: currentUser.phone_number || 
                          currentUser.phone || 
                          order.shipping_address?.phone_number || 'N/A',
              img: getBestProfilePhoto(currentUser) || avatar3,
              description: currentUser.role || 
                          currentUser.user_type || 
                          'Customer'
            });
          }
        } else {
          setError(response.message || "Failed to load order data");
        }
      } catch (err) {
        setError(err.message || "Network error");
      } finally {
        setLoading(false);
      }
    };

    loadOrderData();
  }, [orderId]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Invoice" pageTitle="Order Invoice" />
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading invoice...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Invoice" pageTitle="Order Invoice" />
          <div className="text-center py-5">
            <div className="alert alert-danger">
              <h4>Error Loading Invoice</h4>
              <p>{error || "Order not found"}</p>
              <Link to="/order-list" className="btn btn-primary">
                Back to Orders
              </Link>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      'succeeded': { class: 'bg-success-subtle text-success', text: 'Paid' },
      'pending': { class: 'bg-warning-subtle text-warning', text: 'Pending' },
      'failed': { class: 'bg-danger-subtle text-danger', text: 'Failed' },
      'open': { class: 'bg-info-subtle text-info', text: 'Open' }
    };
    
    const statusInfo = statusMap[status] || { class: 'bg-secondary-subtle text-secondary', text: status };
    return <span className={`badge ${statusInfo.class} fs-11`}>{statusInfo.text}</span>;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Invoice" pageTitle="Order Invoice" />
        
        <Row className="justify-content-center">
          <Col xxl={9}>
            <Card id="demo">
              <CardHeader className="border-bottom-dashed p-4">
                <div className="d-sm-flex">
                  <div className="flex-grow-1">
                    <img
                      src={logoDark}
                      className="card-logo card-logo-dark"
                      alt="logo dark"
                      height="17"
                    />
                    <img
                      src={logoLight}
                      className="card-logo card-logo-light"
                      alt="logo light"
                      height="17"
                    />
                    <div className="mt-sm-5 mt-4">
                      <h6 className="text-muted text-uppercase fw-semibold">
                        Address
                      </h6>
                      <p className="text-muted mb-1">
                        Seed Nations Headquarters
                      </p>
                      <p className="text-muted mb-0">
                        <span>Email:</span> support@seednations.com
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 mt-sm-0 mt-3">
                    <h6>
                      <span className="text-muted fw-normal">
                        Legal Registration No:
                      </span>{" "}
                      <span id="legal-register-no">SN-2024-001</span>
                    </h6>
                    <h6>
                      <span className="text-muted fw-normal">Email:</span>{" "}
                      <span id="email">support@seednations.com</span>
                    </h6>
                    <h6>
                      <span className="text-muted fw-normal">Website:</span>{" "}
                      <Link to="#" className="link-primary">
                        www.seednations.com
                      </Link>
                    </h6>
                    <h6 className="mb-0">
                      <span className="text-muted fw-normal">Contact No:</span>{" "}
                      <span id="contact-no"> +1 (555) 123-4567</span>
                    </h6>
                  </div>
                </div>
              </CardHeader>
              
              <CardBody className="p-4">
                <Row className="g-3">
                  <Col lg={3} xs={6}>
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      Invoice No
                    </p>
                    <h5 className="fs-14 mb-0">
                      <span id="invoice-no">{orderData.order_reference_id}</span>
                    </h5>
                  </Col>
                  <Col lg={3} xs={6}>
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      Date
                    </p>
                    <h5 className="fs-14 mb-0">
                      <span id="invoice-date">{formatDate(orderData.created_at)}</span>
                    </h5>
                  </Col>
                  <Col lg={3} xs={6}>
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      Payment Status
                    </p>
                    {getPaymentStatusBadge(orderData.stripe_payment_status || orderData.payment_object?.status)}
                  </Col>
                  <Col lg={3} xs={6}>
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      Total Amount
                    </p>
                    <h5 className="fs-14 mb-0">
                      $<span id="total-amount">{orderData.total_price}</span>
                    </h5>
                  </Col>
                </Row>
              </CardBody>
              
              <CardBody className="p-4 border-top border-top-dashed">
                <Row className="g-3">
                  <Col sm={6}>
                    <h6 className="text-muted text-uppercase fw-semibold mb-3">
                      Billing Address
                    </h6>
                    <p className="fw-medium mb-2">
                      {orderData.billing_address?.full_name || orderData.shipping_address?.full_name || 'N/A'}
                    </p>
                    <p className="text-muted mb-1">
                      {orderData.billing_address?.address_line1 || orderData.shipping_address?.address_line1 || 'N/A'}
                    </p>
                    <p className="text-muted mb-1">
                      {orderData.billing_address?.city || orderData.shipping_address?.city || 'N/A'}, {orderData.billing_address?.country || orderData.shipping_address?.country || 'N/A'} - {orderData.billing_address?.postal_code || orderData.shipping_address?.postal_code || 'N/A'}
                    </p>
                    <p className="text-muted mb-1">
                      <span>Phone: </span>
                      <span id="billing-phone-no">
                        {orderData.billing_address?.phone_number || orderData.shipping_address?.phone_number || 'N/A'}
                      </span>
                    </p>
                  </Col>
                  <Col sm={6}>
                    <h6 className="text-muted text-uppercase fw-semibold mb-3">
                      Shipping Address
                    </h6>
                    <p className="fw-medium mb-2" id="shipping-name">
                      {orderData.shipping_address?.full_name || 'N/A'}
                    </p>
                    <p className="text-muted mb-1">
                      {orderData.shipping_address?.address_line1 || 'N/A'}
                      {orderData.shipping_address?.address_line2 && (
                        <span>, {orderData.shipping_address.address_line2}</span>
                      )}
                    </p>
                    <p className="text-muted mb-1">
                      {orderData.shipping_address?.city || 'N/A'}, {orderData.shipping_address?.country || 'N/A'} - {orderData.shipping_address?.postal_code || 'N/A'}
                    </p>
                    <p className="text-muted mb-1">
                      <span>Phone: </span>
                      <span id="shipping-phone-no">
                        {orderData.shipping_address?.phone_number || 'N/A'}
                      </span>
                    </p>
                  </Col>
                </Row>
              </CardBody>
              
              <CardBody className="p-4">
                <div className="table-responsive">
                  <Table className="table-borderless text-center table-nowrap align-middle mb-0">
                    <thead>
                      <tr className="table-active">
                        <th scope="col" style={{ width: "50px" }}>
                          #
                        </th>
                        <th scope="col">Product Details</th>
                        <th scope="col">Rate</th>
                        <th scope="col">Quantity</th>
                        <th scope="col" className="text-end">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderData.order_lines?.map((item, index) => (
                        <tr key={item.id}>
                          <th scope="row">{index + 1}</th>
                          <td className="text-start">
                            <span className="fw-medium">
                              {item.product?.name || 'Product'}
                            </span>
                            <p className="text-muted mb-0">
                              {item.edition?.edition_name && (
                                <span>Edition: {item.edition.edition_name}</span>
                              )}
                              {item.edition?.sku && (
                                <span> | SKU: {item.edition.sku}</span>
                              )}
                            </p>
                          </td>
                          <td>${item.price}</td>
                          <td>{item.quantity}</td>
                          <td className="text-end">${item.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="border-top border-top-dashed mt-2">
                  <Table className="table-borderless text-center table-nowrap align-middle mb-0">
                    <tbody>
                      <tr>
                        <td colSpan="3"></td>
                        <td colSpan="2" className="fw-medium p-0">
                          <Table className="table-borderless text-start table-nowrap align-middle mb-0">
                            <tbody>
                              <tr>
                                <td>Sub Total :</td>
                                <td className="text-end">${orderData.subtotal_price}</td>
                              </tr>
                              {orderData.applied_discount > 0 && (
                                <tr>
                                  <td>Discount :</td>
                                  <td className="text-end">-${orderData.applied_discount}</td>
                                </tr>
                              )}
                              {orderData.delivery_fee > 0 && (
                                <tr>
                                  <td>Delivery Fee :</td>
                                  <td className="text-end">${orderData.delivery_fee}</td>
                                </tr>
                              )}
                              {orderData.applied_tax > 0 && (
                                <tr>
                                  <td>Estimated Tax :</td>
                                  <td className="text-end">${orderData.applied_tax}</td>
                                </tr>
                              )}
                              <tr className="border-top border-top-dashed">
                                <th scope="row">Total (USD) :</th>
                                <th className="text-end">${orderData.total_price}</th>
                              </tr>
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
                <div className="mt-3">
                  <div className="d-print-none d-flex flex-wrap gap-2">
                    <Button
                      type="button"
                      className="btn btn-primary"
                      onClick={printInvoice}
                    >
                      <i className="ri-printer-line align-bottom me-1"></i> Print
                    </Button>
                    <Button
                      type="button"
                      className="btn btn-success"
                      onClick={printInvoice}
                    >
                      <i className="ri-download-2-line align-bottom me-1"></i> Download
                    </Button>
                    <Link
                      to={`/order-detail?orderId=${orderId}`}
                      className="btn btn-secondary"
                    >
                      <i className="ri-arrow-left-line align-bottom me-1"></i> Back to Order
                    </Link>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OrderInvoice;
